import React from 'react';
import clsx from 'clsx';
import SurveyItem from '../../util/SurveyItem';
import { Text } from '@lifeomic/chroma-react/components/Text';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import MarkdownViewer from '../../../MarkdownViewer';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../util/testProps';

export const testIds = {
  display: (linkId: string) => getSurveyItemTestId(linkId),
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(4),
      '& h1, & h2, & h3, & h4, & h5, & h6': {
        fontWeight: theme.typography.fontWeightMedium,
      },
      '& blockquote': {
        background: theme.palette.darkGraphite[50],
        padding: theme.spacing(2),
      },
    },
    denseMarginRoot: {
      marginTop: theme.spacing(2),
    },
  });

export interface DisplayOwnProps {
  surveyItem: SurveyItem;
}

export type DisplayProps = WithStyles<typeof styles> &
  WithCheckForEnablementInjectedProps &
  DisplayOwnProps;

const Display: React.FunctionComponent<DisplayProps> = (
  props: DisplayProps,
) => {
  if (!props.isEnabled) return null;

  // Dense only tightens the top margin. Swapping the class out instead of
  // adding it dropped the secondary color and heading weights, so a dense
  // display rendered darker than the section headings it sits among.
  const rootClass = clsx(
    props.classes.root,
    props.surveyItem.hasDenseMargins && props.classes.denseMarginRoot,
  );

  if (props.surveyItem.hasText && props.surveyItem.markdownDisplay) {
    return (
      <div
        className={rootClass}
        {...getTestProps(testIds.display(props.surveyItem.link))}
      >
        <MarkdownViewer markdown={props.surveyItem.text} />
      </div>
    );
  } else if (props.surveyItem.hasText) {
    const variant = props.surveyItem.displayTypographyVariant || 'subbody';
    return (
      <div
        className={rootClass}
        {...getTestProps(testIds.display(props.surveyItem.link))}
      >
        <Text
          size={variant}
          weight={props.surveyItem.hasBoldTitle ? 'bold' : undefined}
        >
          {props.surveyItem.text}
        </Text>
      </div>
    );
  } else {
    return null;
  }
};

export default withStyles(styles)(withCheckForEnablement(Display));
